import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'
import { type Address, getAddress } from 'viem'
import { E_EARN_VAULT_CHAIN_ID } from '@/config/consts'
import { publicEulerRoutes } from '@/lib/euler-api/publicRouteSpecs'
import type { EulerIndexerEVault, EulerIndexerEVaultsResponse, NormalizedEVaultInfo } from './types'

/**
 * Query key for Euler Indexer EVaults
 */
export function eulerEVaultsQueryKey(chainId: number): readonly [string, string, number] {
  return ['euler', 'evaults', chainId] as const
}

/**
 * Fetch all EVaults from Euler Indexer API
 */
async function fetchEulerIndexerEVaults(chainId: number): Promise<EulerIndexerEVault[]> {
  // Server hardcodes take/skip via route spec — client only sends chainId
  const url = publicEulerRoutes.earnVaults.clientUrl(chainId)

  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch EVaults from Euler Indexer: ${response.status} ${response.statusText}`)
  }

  const data: EulerIndexerEVaultsResponse = await response.json()

  if (!data || !Array.isArray(data.items)) {
    throw new Error('Invalid response format from Euler Indexer')
  }

  return data.items
}

/**
 * Transform Euler Indexer EVaults to normalized format
 */
function normalizeEVaults(vaults: EulerIndexerEVault[]): Map<Address, NormalizedEVaultInfo> {
  const vaultMap = new Map<Address, NormalizedEVaultInfo>()

  for (const vault of vaults) {
    try {
      const normalizedAddress = getAddress(vault.vault) as Address
      vaultMap.set(normalizedAddress, {
        chainId: vault.chainId,
        address: normalizedAddress,
        name: vault.vaultName ?? 'Unknown Vault',
        symbol: vault.vaultSymbol ?? 'UNKNOWN',
        decimals: vault.vaultDecimals ?? 18,
        asset: vault.asset ? (getAddress(vault.asset) as Address) : null,
        assetName: vault.assetName,
        assetSymbol: vault.assetSymbol,
        assetDecimals: vault.assetDecimals,
        totalShares: BigInt(vault.totalShares || '0'),
        totalAssets: BigInt(vault.totalAssets || '0'),
        totalAssetsUSD: vault.totalAssetsUSD ?? 0,
        availableAssets: BigInt(vault.availableAssets || '0'),
        availableAssetsUSD: vault.availableAssetsUSD ?? 0,
        apy7d: vault.apy7d,
        apy30d: vault.apy30d,
        apy90d: vault.apy90d,
        apyCurrent: vault.apyCurrent,
        strategies: vault.strategies ?? [],
        owner: vault.owner ? (getAddress(vault.owner) as Address) : null,
        curator: vault.curator ? (getAddress(vault.curator) as Address) : null,
        guardian: vault.guardian ? (getAddress(vault.guardian) as Address) : null,
        performanceFee: vault.performanceFee ?? '0',
        timelock: vault.timelock ?? '0',
        supplyQueue: vault.supplyQueue?.map((addr) => getAddress(addr) as Address) ?? null,
        source: 'indexer',
      })
    } catch {
      // Skip invalid addresses
      console.warn(`Invalid EVault address: ${vault.vault}`)
    }
  }

  return vaultMap
}

/**
 * Query options for fetching all Euler Indexer EVaults
 */
export function eulerEVaultsQueryOptions(chainId: number = E_EARN_VAULT_CHAIN_ID) {
  return queryOptions({
    queryKey: eulerEVaultsQueryKey(chainId),
    queryFn: async () => {
      const vaults = await fetchEulerIndexerEVaults(chainId)
      return normalizeEVaults(vaults)
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  })
}

/**
 * Hook to fetch all EVaults from Euler Indexer
 */
export function useEulerIndexerEVaults(chainId: number = E_EARN_VAULT_CHAIN_ID) {
  return useQuery(eulerEVaultsQueryOptions(chainId))
}

/**
 * Hook to get a specific EVault by address from the cached vault list
 * Returns undefined if not found in cache
 */
export function useEulerIndexerEVault(address: Address | undefined, chainId: number = E_EARN_VAULT_CHAIN_ID) {
  const { data: vaultMap } = useEulerIndexerEVaults(chainId)

  if (!address || !vaultMap) {
    return undefined
  }

  try {
    const normalizedAddress = getAddress(address) as Address
    return vaultMap.get(normalizedAddress)
  } catch {
    return undefined
  }
}

/**
 * Prefetch Euler Indexer EVaults - useful for SSR or initial load
 */
export function usePrefetchEulerEVaults(chainId: number = E_EARN_VAULT_CHAIN_ID) {
  const queryClient = useQueryClient()

  return () => {
    return queryClient.prefetchQuery(eulerEVaultsQueryOptions(chainId))
  }
}

/**
 * Get EVault from cache synchronously (for use in callbacks)
 */
export function getEVaultFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  chainId: number,
  address: Address,
): NormalizedEVaultInfo | undefined {
  const vaultMap = queryClient.getQueryData<Map<Address, NormalizedEVaultInfo>>(eulerEVaultsQueryKey(chainId))

  if (!vaultMap) {
    return undefined
  }

  try {
    const normalizedAddress = getAddress(address) as Address
    return vaultMap.get(normalizedAddress)
  } catch {
    return undefined
  }
}

/**
 * Set EVault in cache - useful after onchain fallback fetch
 */
export function setEVaultInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  chainId: number,
  vault: NormalizedEVaultInfo,
): void {
  const queryKey = eulerEVaultsQueryKey(chainId)
  const currentMap = queryClient.getQueryData<Map<Address, NormalizedEVaultInfo>>(queryKey)

  if (currentMap) {
    const newMap = new Map(currentMap)
    newMap.set(vault.address, vault)
    queryClient.setQueryData(queryKey, newMap)
  } else {
    // If no cache exists, create new map with this vault
    const newMap = new Map<Address, NormalizedEVaultInfo>()
    newMap.set(vault.address, vault)
    queryClient.setQueryData(queryKey, newMap)
  }
}
